import './styles.scss';

import { useMemo, useRef } from 'react';

import { Card } from 'react-bootstrap';
import CardHeader from '@/features/DriveTimeTool/components/CardHeader';
import { LoadingBlur } from '@/components/LoadingSpinner';
import TimeFrameDropdown from '@/features/DriveTimeTool/components/TimeFrameDropdown';
import TimeIntervalStatsTable from '@/features/DriveTimeTool/components/TimeIntervalStats/TimeIntervalStatsTable';
import Toast from '@/models/Toast';
import { createNotification } from '@/utils';
import { exportPredictiveDriveTimeReportToCsv } from '../../api/services/getPredictiveDriveTimeReportAsCsv';
import { useDriveTimeTool } from '@/features/DriveTimeTool';

const TimeIntervalStats = (): JSX.Element => {
  const [{ loading, timeFrameId }, setState] = useDriveTimeTool(({ state, setState }) => [
    { loading: state.loading.intervals, timeFrameId: state.timeFrameId, selected: state.selected },
    setState,
  ]);
  const contentRef = useRef<HTMLDivElement>(null);

  const onToggleTimeFrameModal = () => {
    useDriveTimeTool.getState().setState((state) => ({ ...state, showTimeFrameModal: !state.showTimeFrameModal }));
  };

  const onSelectTimeFrame = async (value: string) => {
    if (!value) value = 'default';
    if (value === timeFrameDropdownValue) return;
    if (value === 'default') return setState((state) => ({ ...state, timeFrameId: null }));
    setState((state) => ({ ...state, timeFrameId: value }));
  };

  const timeFrameDropdownValue = useMemo(() => {
    const parts = timeFrameId?.split('||') || [];
    if (parts.length === 2) return timeFrameId;
    if (parts.length > 2) return `${parts[0]}||${parts[1]}`;
    return 'default';
  }, [timeFrameId]);

  return (
    <div className="TimeIntervalStats" ref={contentRef}>
      <Card className="time-interval-card">
        <CardHeader
          icon="sv sv-grid"
          tooltip={tooltipContent}
          right={
            <div className="d-flex align-items-center flex-nowrap gap-2 text-nowrap">
              <TimeFrameDropdown
                placeholder={timeFrameId?.split('||')[1]}
                value={timeFrameDropdownValue}
                onChange={onSelectTimeFrame}
                options={{ showClearButton: timeFrameId !== null }}
              />
              <button className="btn btn-outline-gray-subtle text-dark" onClick={onToggleTimeFrameModal}>
                <i className="sv sv-cog me-2" />
                Configure Time Frames
              </button>
              <button
                className="btn btn-outline-gray-subtle text-dark "
                onClick={() => {
                  const route = useDriveTimeTool
                    .getState()
                    .state.history.flatMap((item) => item.routes)
                    .find((item) => item.id === useDriveTimeTool.getState().state.selected);
                  if (!route) {
                    return createNotification(
                      'Failed to exporting drive time report: History not found. Please try again.',
                      Toast.Type.DANGER,
                      'Export Drive Time Report'
                    );
                  }
                  exportPredictiveDriveTimeReportToCsv(
                    route.id,
                    route.manifestHistoryId ? 'manifestHistoryRouteId' : 'queryHistoryId',
                    useDriveTimeTool.getState().state.timeFrameId?.split('||')[0]
                  );
                }}
              >
                <i className="sv sv-download2 me-2" />
                Export
              </button>
            </div>
          }
        >
          Time Interval Statistics
        </CardHeader>
        <Card.Body>
          <LoadingBlur loading={loading} />
          <TimeIntervalStatsTable />
        </Card.Body>
      </Card>
    </div>
  );
};

const tooltipContent = (
  <div className="tooltip-content">
    <h4>About Time Interval Statistics</h4>
    <p>This table shows your exact uploaded data organized by time frames throughout the day.</p>
    <p className="muted-text">Select queries to view your precise statistics without any modifications or calculations.</p>
  </div>
);

export default TimeIntervalStats;
